import {
    CALLBACK_NOT_SET, ERR_ACC_BLOCKED, ERR_ACCESS_DENIED,
    ERR_ACCESS_UNVERIFIED, ERR_ACCOUNT_FAILED, ERR_APP_ACCESS_PROHIBITED,
    ERR_APP_DISABLED, ERR_AWAITING_RESET, ERR_DEVICE_APPROVAL_PENDING,
    ERR_DEVICE_APPROVAL_REJECTED, ERR_DEVICE_NOT_FOUND, ERR_DEVICE_USER_MISMATCH,
    ERR_FAILED_GET_COMPANIES,
    ERR_FORGOT_EXPIRED, ERR_FORGOT_INVALID, ERR_FORGOT_USED, ERR_INVALID_DEVICE_TYPE,
    ERR_INVALID_PASS, ERR_INVALID_PIN, ERR_IP_BANNED, ERR_IP_NOT_WHITELISTED,
    ERR_MAX_DEVICE_REACHED, ERR_RATE_LIMIT, ERR_SESSION_EXPIRED,
    ERR_SESSION_INVALID, ERR_UNAUTHORIZED_DEVICE, USER_NOT_FOUND,
    ERR_DATA, ERR_INTERNAL
} from "../constants";

export type ErrorContext = {
    retries_left?: number;
    app?: string;
    [key: string]: any;
};

export const getErrorMessage = (code: string, context?: ErrorContext): string => {
    switch (code) {
        // Login/Auth Errors
        case USER_NOT_FOUND:
            return "User not found.";
        case ERR_ACCOUNT_FAILED:
        case ERR_INVALID_PIN:
            return (context?.retries_left || 0) + " attempts remaining.";
        case ERR_ACC_BLOCKED:
            return "Your account is blocked. Please contact your Administrator.";
        case ERR_AWAITING_RESET:
            return "Password reset initiated. Please check your email to set a new password.";
        case ERR_RATE_LIMIT:
            return "Too many login attempts. Please try again in 15 minutes.";
        case ERR_INVALID_PASS:
            return "Invalid password.";

        // Session Errors
        case ERR_SESSION_EXPIRED:
            return "Session expired. Please login again.";
        case ERR_SESSION_INVALID:
            return "Session invalid.";

        // Security / Restriction Errors
        case ERR_IP_NOT_WHITELISTED:
            return "Access denied: Your IP address is not whitelisted for this company.";
        case ERR_DEVICE_APPROVAL_PENDING:
            return "Your device is pending approval for this company. Please wait for an administrator to approve it.";
        case ERR_DEVICE_APPROVAL_REJECTED:
            return "Your device access has been rejected by this company. Please contact your administrator or try again later.";

        // Registration Errors
        case "user_already_enrolled":
            return "Username already taken.";
        case "email_already_inuse":
            return "Email is already in use.";
        case "verified":
        case "invite_expired":
            return "Unable to register user. Invitation already expired.";

        // Forgot/Reset Password Errors
        case ERR_FORGOT_EXPIRED:
            return "Reset link has expired.";
        case ERR_FORGOT_INVALID:
            return "Invalid reset link.";
        case ERR_FORGOT_USED:
            return "Reset link already used.";

        // Default or unknown errors
        case "incomplete_param":
            return "Required fields are missing.";
        case ERR_DATA:
            return "Server data error. Please try again later.";
        case ERR_INTERNAL:
            return "Internal server error. Please contact support.";
        default:
            return "";
    }
};

export const getServerErrorData = (code: string, context?: ErrorContext): { title: string; message: string } => {
    let title = "Server Error";
    let message = "Server is not responding. Please try again later.";

    switch (code) {
        case ERR_ACCESS_DENIED:
            title = "Access Not Available [01]";
            message = context?.app
                ? `You don't have access to ${context.app} app.`
                : "You don't have access to this app.";
            message += " Please contact your administrator.";
            break;
        case CALLBACK_NOT_SET:
            title = "Application Not Configured";
            message = "This app is not properly configured. Please contact your administrator.";
            break;
        case ERR_APP_ACCESS_PROHIBITED:
            title = "Access Not Allowed";
            message = "App is not accesible at this time. Please make sure you are subscribed to this app. Contact your administrator for more details.";
            break;
        case ERR_APP_DISABLED:
            title = "Application Not Available [02]";
            message = "This app is not available at this time. Please contact your administrator.";
            break;
        case ERR_INVALID_DEVICE_TYPE:
            title = "Device Invalid";
            message = "Unable to verify device type.";
            break;
        case ERR_MAX_DEVICE_REACHED:
            title = "Maximum Device Reached";
            message = "Unable to add device. Maximum number of devices reached. Remove device to continue.";
            break;
        case ERR_UNAUTHORIZED_DEVICE:
            title = "Unauthorized";
            message = "Device is unauthorized. This will be recorded. Please contact your administrator.";
            break;
        case ERR_ACCESS_UNVERIFIED:
            title = "Access Not Verified";
            message = "You need to verify your access. If you received an invite via email please accept or contact your administrator for more info.";
            break;
        case ERR_IP_BANNED:
            title = "Unauthorized Activity";
            message = "Unable to continue. Please contact your administrator";
            break;
        case ERR_IP_NOT_WHITELISTED:
            title = "Network Restricted";
            message = "Unable to continue. Please contact your administrator";
            break;
        case ERR_FAILED_GET_COMPANIES:
            title = "Unable to Continue";
            message = "Please contact your administrator and check if you are already invited as user.";
            break;
    }

    return { title, message };
};
